import type { ProjectGlobalData } from '../../types';
import type { ModuleStats } from '../project-hub/ModuleTile';
import type { AmpelFarbe, PlanungAmpel } from './planungAmpel';
import { leitfadenStand, weiterText } from './leitfaden';
import type { LeitfadenEingang } from './leitfadenTypen';

/**
 * Reine Helfer fuer die Anzeige der Planungs-Ampel (Stufe 1.5/1.6,
 * docs/planungsphase-automatisierung-plan.md): Hub-Kachel, Dashboard-Liste
 * und Aufgabenliste lesen dieselben Textbausteine und Farbtabellen, damit
 * drei Stellen nie drei verschiedene Worte fuer denselben Zustand finden.
 *
 * Die Ampel selbst rechnet `planungAmpel` (planungAmpel.ts), die naechste
 * Handlung `leitfadenStand`/`weiterText` (leitfaden.ts, Stufe L3); hier steht
 * nur, wie beides gezeigt wird. Ohne React, ohne Supabase.
 */

/** Der Teil der Ampel, den die Anzeige liest — Tests brauchen so kein volles Objekt. */
export type AmpelAuszug = Pick<PlanungAmpel, 'farbe' | 'gruende' | 'quote' | 'abgelaufeneFristen'>;

/** Hinweis 1.6: LV mit Planungsleistung, aber noch keine Phase angelegt. */
export const PLANUNGSLEISTUNG_OHNE_PHASE = 'Planungsleistung beauftragt, Planungsphase noch nicht angelegt';

/**
 * Rot, gelb und fehler verlangen einen Blick ins Modul. `fehler` zaehlt dazu,
 * weil ein nicht lesbarer Stand nie als „alles in Ordnung" durchgehen darf
 * (fail-open: nie gruen, wenn nichts geladen werden konnte).
 */
export const hatHandlungsbedarf = (farbe: AmpelFarbe): boolean =>
    farbe === 'rot' || farbe === 'gelb' || farbe === 'fehler';

/** Sortierrang: rot vor gelb; der technische Fehler dahinter, dann die Ruhe-Zustaende. */
export const AMPEL_RANG: Record<AmpelFarbe, number> = { rot: 0, gelb: 1, fehler: 2, grau: 3, gruen: 4 };

/**
 * Tailwind kompiliert nur Klassen, die WOERTLICH im Quelltext stehen —
 * deshalb ausgeschrieben statt aus dem Farbnamen zusammengesetzt.
 * Der Fehlerpunkt ist grau mit rotem Ring: kein echter Befund, aber auch
 * keine Entwarnung.
 */
export const AMPEL_PUNKT: Record<AmpelFarbe, string> = {
    rot: 'bg-red-500',
    gelb: 'bg-amber-400',
    gruen: 'bg-emerald-500',
    grau: 'bg-slate-300',
    fehler: 'bg-slate-400 ring-2 ring-red-200',
};

export const AMPEL_TEXT: Record<AmpelFarbe, string> = {
    rot: 'text-red-700',
    gelb: 'text-amber-700',
    gruen: 'text-emerald-700',
    grau: 'text-slate-500',
    fehler: 'text-red-700',
};

const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/;

/** Nur ein YYYY-MM-DD, alles andere (leer, Unsinn) wird null. */
export const isoTag = (wert: string | null | undefined): string | null => {
    const t = (wert || '').slice(0, 10);
    return ISO_TAG.test(t) ? t : null;
};

/**
 * Baustart fuer die harten Fristen der Ampel: der Ausfuehrungsbeginn, sonst
 * der Projektstart (Entscheid E1 — Rueckfall Projektstart). Beides aus dem
 * geladenen Stand, nichts wird geraten.
 */
export const baustartAusGlobalData = (
    g: Pick<ProjectGlobalData, 'executionStartDate' | 'startDate'> | null | undefined,
): string | null => isoTag(g?.executionStartDate) ?? isoTag(g?.startDate);

/** Steht eine Planungsleistung im LV (Zaehler der Kachel, `planning_general`)? */
export const lvHatPlanungsleistung = (stats: ModuleStats): stats is { used: number; contracted: number } =>
    !!stats && stats.contracted > 0;

/** „7 von 32 geklärt" — ohne Fragen sagt die Zeile das auch. */
export const quoteText = (quote: AmpelAuszug['quote']): string =>
    quote.gesamt > 0 ? `${quote.geklaert} von ${quote.gesamt} geklärt` : 'noch keine Fragen';

/**
 * „3 abgelaufene Fristen — im Modul feststellen". Festgestellt wird NUR im
 * Modul (Entscheid E9: der Mensch stellt fest, kein Sammelknopf im Dashboard).
 */
export const fristenHinweis = (anzahl: number): string => {
    if (!anzahl || anzahl <= 0) return '';
    return anzahl === 1
        ? '1 abgelaufene Frist: im Modul feststellen'
        : `${anzahl} abgelaufene Fristen: im Modul feststellen`;
};

/** Der Grund, der die Farbe gemacht hat; sonst der erste; sonst leer. */
export const hauptgrund = (ampel: Pick<AmpelAuszug, 'farbe' | 'gruende'>): string =>
    (ampel.gruende.find(g => g.farbe === ampel.farbe) ?? ampel.gruende[0])?.text ?? '';

/** Alle Gruende untereinander — fuer den Tooltip der Kachel. */
export const gruendeTooltip = (ampel: Pick<AmpelAuszug, 'gruende'>): string | undefined =>
    ampel.gruende.length ? ampel.gruende.map(g => g.text).join('\n') : undefined;

// ═══════════════════════════════════════════════════════════════════════════
// WEITER — die naechste Handlung des Leitfadens ausserhalb des Moduls
// (docs/planungsphase-leitfaden-plan.md, Stufe L3, Entscheid E-L8)
// ═══════════════════════════════════════════════════════════════════════════

/** Was der Weiter-Text aus der Lage liest — genau das, was auch die Ampel
 *  bekommt. „Rest bleibt offen" (Station 3) steht am Kopfsatz
 *  (`phase.merkmale.leitfadenRestOffen`) und kommt damit von selbst mit; die
 *  Checkliste des Einzelprojekts bleibt draussen. Kachel, Sammelabfrage und
 *  Modul sagen so fuer jeden Kollegen und jedes Geraet dasselbe. */
export type WeiterLage = Pick<LeitfadenEingang, 'phase' | 'fragen' | 'hinweise' | 'heuteIso' | 'baustartIso'>;

/**
 * „Weiter: Antworten einarbeiten · 2 Fristen abgelaufen — feststellen" aus
 * derselben Lage, die die Ampel liest. Hub-Kachel und Sammelabfrage rufen
 * beide hier, damit sie wortgleich sind.
 *
 * Leer ohne Phase (dann spricht der Hinweis 1.6 bzw. die Ampel), wenn nichts
 * mehr zu tun ist, und wenn die Rechnung an einer kaputten Zeile scheitert —
 * nie geraten. Die Ampel bleibt davon unberuehrt.
 */
export const weiterAusLage = (lage: WeiterLage): string => {
    if (!lage.phase) return '';
    try {
        return weiterText(leitfadenStand({
            phase: lage.phase,
            fragen: lage.fragen,
            hinweise: lage.hinweise,
            heuteIso: lage.heuteIso,
            baustartIso: lage.baustartIso,
        }));
    } catch (e) {
        console.warn('Weiter-Text der Planungsphase konnte nicht berechnet werden.', e);
        return '';
    }
};

/** Weiter-Text eines Projekts aus der Sammelabfrage (`PlanungsAmpeln.weiter`):
 *  nur eigene Eintraege, nur Zeichenketten — eine Projektkennung trifft nie
 *  Geerbtes wie `constructor`; fehlt er, leer. */
export const weiterFuer = (weiter: Readonly<Record<string, string>> | null | undefined, projectId: string): string => {
    if (!weiter || !projectId || !Object.prototype.hasOwnProperty.call(weiter, projectId)) return '';
    const w = weiter[projectId];
    return typeof w === 'string' ? w : '';
};

/** `weiterText` baut „<Kopf> · <Stand>"; die Stationstitel tragen kein „·"
 *  (Test), deshalb trennt das ERSTE Vorkommen Kopf und Stand. */
const WEITER_TRENNER = ' · ';

const einzeilig = (t: string | null | undefined): string => String(t ?? '').replace(/\s+/g, ' ').trim();

/**
 * Der Weiter-Text als Zeile UNTER dem Grund: einzeilig, und wiederholt sein
 * Stand nur, was der Grund schon sagt (Ampel und Station 5 sagen beide
 * „3 Fragen noch nicht gestellt"), bleibt nur der Kopf — „Weiter:
 * Klärungslisten versenden". Gekuerzt wird nur, was doppelt dastuende; den
 * Volltext traegt der `title`.
 */
export const weiterKurz = (weiter: string | null | undefined, grund: string | null | undefined): string => {
    const voll = einzeilig(weiter);
    const g = einzeilig(grund);
    const i = voll.indexOf(WEITER_TRENNER);
    if (!g || i < 0) return voll;
    const stand = voll.slice(i + WEITER_TRENNER.length);
    return stand && g.includes(stand) ? voll.slice(0, i) : voll;
};

/** Zeilen unter Titel bzw. Projektname — fuer Kachel und Dashboard-Liste. */
export interface HinweisZeilen {
    /** Erste Zeile: der Grund der Ampel; leer, wenn er nichts zu sagen hat. */
    grund: string;
    /** Darunter „Weiter: …" (`weiterKurz`); leer ohne Weiter-Text. */
    weiter: string;
    /** Der ungekuerzte Weiter-Text fuer den `title`. */
    weiterVoll: string;
}

/**
 * Hinweiszeilen der Hub-Kachel (Entscheid E-L8): bei roter/gelber Ampel der
 * Hauptgrund und darunter „Weiter: …"; bei gruener Ampel nur „Weiter: …",
 * wenn es etwas gibt. Der Weiter-Text kommt hinzu, er ersetzt den Grund nie.
 * (Den Hinweis 1.6 ohne Phase und den Lesefehler entscheidet die Kachel
 * vorher — dort ist der Weiter-Text ohnehin leer.)
 */
export const kachelHinweisZeilen = (
    ampel: Pick<AmpelAuszug, 'farbe' | 'gruende'>,
    weiter: string | null | undefined,
): HinweisZeilen => {
    const grund = hatHandlungsbedarf(ampel.farbe) ? hauptgrund(ampel) : '';
    return { grund, weiter: weiterKurz(weiter, grund), weiterVoll: einzeilig(weiter) };
};

/** Zeilen je Projekt in „Planungsphase — Handlungsbedarf". */
export interface ListenZeilen extends HinweisZeilen {
    /** „2 abgelaufene Fristen — im Modul feststellen" — nur ohne Weiter-Text. */
    fristen: string;
}

/**
 * Zeilen je Projekt der Dashboard-Liste: der Grund (sonst die
 * Klaerungsquote), darunter „Weiter: …". Der Fristenhinweis steht nur noch,
 * wenn es keinen Weiter-Text gibt: sind Fristen abgelaufen, IST das die
 * naechste Handlung (Station 6 „… feststellen") — nur eine rote Pflicht im
 * Fenster vor der Vorankuendigungsfrist (Station 2) geht ihr voraus. Sonst
 * stuende „feststellen" zweimal untereinander. Dieselbe Regel wie
 * `planungAufgabenDetail`.
 */
export const listenHinweisZeilen = (ampel: AmpelAuszug, weiter: string | null | undefined): ListenZeilen => {
    const grund = hauptgrund(ampel) || quoteText(ampel.quote);
    const kurz = weiterKurz(weiter, grund);
    return {
        grund,
        weiter: kurz,
        weiterVoll: einzeilig(weiter),
        fristen: kurz ? '' : fristenHinweis(ampel.abgelaufeneFristen),
    };
};

/**
 * Untertitel der Hub-Kachel (Entscheid E12): die LV-Menge steht hier,
 * zweitrangig, statt als Zaehler oben rechts — vorn stehen Ampel und
 * Klaerungsquote. Ohne LV-Position bleibt der bisherige Text.
 */
export const planungUntertitel = (stats: ModuleStats, isReadOnly: boolean): string => {
    const basis = isReadOnly ? 'Nur-Ansicht: Klärungsstand ansehen' : 'Klären & dokumentieren nach § 3 BaustellV';
    return lvHatPlanungsleistung(stats) ? `LV: ${stats.used} von ${stats.contracted} gebucht · ${basis}` : basis;
};

/** Lesefehler der Sammelabfrage in einen Satz — die Form ist dem Aufrufer egal. */
export const lesefehlerText = (fehler: unknown): string | null => {
    if (!fehler) return null;
    if (typeof fehler === 'string') return fehler;
    if (Array.isArray(fehler)) {
        const teile = fehler.map(lesefehlerText).filter((t): t is string => !!t);
        return teile.length ? teile.join(' · ') : null;
    }
    if (fehler instanceof Error) return fehler.message;
    if (typeof fehler === 'object' && 'message' in fehler && typeof (fehler as { message: unknown }).message === 'string') {
        return (fehler as { message: string }).message;
    }
    return String(fehler);
};
